import http from '../client';
import { errorHandler } from '../errorHandler';
import type { ApiResponse, User } from '../types';
import type { DoctorProfile, DoctorProfileData } from './types';

interface GetPendingDoctorsParams {
  page?: number;
  limit?: number;
}

interface PaginatedDoctorsResponse {
  doctors: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ApproveRejectDoctorData {
  admin_notes?: string;
}

export const doctorApi = {
  getPendingDoctors: async (
    params?: GetPendingDoctorsParams,
  ): Promise<ApiResponse<PaginatedDoctorsResponse | User[]>> => {
    try {
      // Build query string from params
      const queryParams = new URLSearchParams();

      // Use defaults if params are not provided or empty
      const page = params?.page && params.page > 0 ? params.page : 1;
      const limit = params?.limit && params.limit > 0 ? params.limit : 10;

      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());

      const queryString = queryParams.toString();
      const url = `/admin/doctors/pending${queryString ? `?${queryString}` : ''}`;

      const response = await http.get(url);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  getPendingDoctorsCount: async (): Promise<ApiResponse<{ count: number }>> => {
    try {
      const response = await http.get('/admin/doctors/pending/count');
      // Match backend shape { success, message, data: { count } } — same unwrap as UserApi.getNewUsersCount
      return {
        success: true,
        data: (response.data?.data ?? response.data) as { count: number },
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  approveDoctor: async (
    profileId: string | number,
    data?: ApproveRejectDoctorData,
  ): Promise<ApiResponse<unknown>> => {
    try {
      const response = await http.post(`/admin/doctors/${profileId}/approve`, {
        admin_notes: data?.admin_notes || '',
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  rejectDoctor: async (
    profileId: string | number,
    data?: ApproveRejectDoctorData,
  ): Promise<ApiResponse<unknown>> => {
    try {
      const response = await http.post(`/admin/doctors/${profileId}/reject`, {
        admin_notes: data?.admin_notes || '',
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  getDoctorProfile: async (
    profileId: string | number,
  ): Promise<ApiResponse<DoctorProfileData | DoctorProfile>> => {
    try {
      const response = await http.get(`/doctor/profile/${profileId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  getAllDoctors: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }): Promise<ApiResponse<User[]>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.status) queryParams.append('status', params.status);

      const response = await http.get(
        `/admin/doctors/all?${queryParams.toString()}`,
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },
};
